import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { isNullOrUndefined } from 'util';
import { TimestampsService } from '../timestamps.service';
import { TimeZoneDescriptor } from '../timezones/time-zone-descriptor.interface';
import { TIME_ZONE_FAMILIES } from '../timezones/time-zones.const';
import { DetectMethodChanges, DetectSetterChanges, groupTimezoneFamilies } from '../utils';

@Component({
   selector: 'ets-timezone-picker',
   templateUrl: 'timezone-picker.component.html',
   styleUrls: ['timezone-picker.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush
})

export class TimezonePickerComponent implements OnInit {
   constructor(
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _timestampsService: TimestampsService
   ) {
      this.timezonesCountriesList = groupTimezoneFamilies();
   }

   @Input() flow: 'col' | 'row' = 'row';

   @Input() showCaption = true;
   
   @Input() showBorder = true;

   @Input() disabled = false;

   timezonesCountriesList: any[];

   timezonesByCountry: TimeZoneDescriptor[] = [];   
   
   private _timezoneCountry: string;
   get timezoneCountry(): string {
      return this._timezoneCountry;
   }

   @DetectSetterChanges()
   set timezoneCountry(value: string) {
      const family = TIME_ZONE_FAMILIES.find(f => f.displayName === value);
      this._timezoneCountry = family ? family.displayName : null;
      this.timezonesByCountry = family ? family.timeZoneDescriptors : [];
   }

   private _timezone: string;
   get timezone(): string {
      return this._timezone;
   }

   @Input()
   @DetectSetterChanges()
   set timezone(value: string) {
      const family = TIME_ZONE_FAMILIES.find(f =>
         f.timeZoneDescriptors.some(t => t.nodaTimeZoneId === value));
      const zone = family && family.timeZoneDescriptors.find(t => t.nodaTimeZoneId === value);

      // Restore all dependent fields before repainting; never emit while applying an input.
      this._timezone = value;
      this._timezoneCountry = family ? family.displayName : null;
      this.timezonesByCountry = family ? family.timeZoneDescriptors : [];
      this._displayTimezone = zone ? zone.displayName : null;
   }

   @DetectMethodChanges()
   onCountrySelected(event: any) {
      if (!event.event || this.timezoneCountry === event.value) {
         return;
      }
      this.timezoneCountry = event.value;
      this._timezone = null;
      this._displayTimezone = null;
      this.timezoneChange.emit(null);
   }

   @DetectMethodChanges()
   onTimezoneSelected(event: any) {
      if (!event.event || this.timezone === event.value) {
         return;
      }
      if (event.value) {
         this.timezone = event.value;
      } else {
         // Clearing a timezone keeps the selected country available for the next choice.
         this._timezone = null;
         this._displayTimezone = null;
      }
      this.timezoneChange.emit(this.timezone);
   }

   @Output() timezoneChange = new EventEmitter<string>();

   private _displayTimezone: string;
   get displayTimezone(): string {
      if (isNullOrUndefined(this._displayTimezone)) {
         return 'Pick timezone...';
      }
      return this._displayTimezone;
   }

   @DetectSetterChanges()
   set displayTimezone(v: string) {
      this._displayTimezone = v;
   }
   

   @DetectMethodChanges()
   ngOnInit() {
      if (!this.timezone) {
         const defaultTimezone = this._timestampsService.getDefaultTimezone();
         if (defaultTimezone) {
            this.timezone = defaultTimezone;
            this.timezoneChange.emit(this.timezone);
         }
      }
    }

   @DetectMethodChanges()
   reset() {
      this.timezone = null;
      this.timezoneChange.emit(null);
   }
}
